import {FC} from 'react'

import {Dropdown, OptionType} from '@/components/inputs/Dropdown'
import {getClassAssistContent} from '@/content/classAssist'
import {useTranslation} from '@/i18n'
import {GUIDE_SELECTION_OVERVIEW_WIDTH} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {useLanguage} from '@/utils/i18n/language'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Text, TextFontWeight, TextVariant} from '../../common/content/Text'

type ClassAssistSelectionProps = {
  industryOptions: OptionType[]
  industryValue: OptionType['value'] | null
  onIndustryChange: (e: any) => void
  sectorOptions: OptionType[]
  sectorValue: OptionType['value'] | null
  onSectorChange: (e: any) => void
}

export const ClassAssistSelection: FC<React.PropsWithChildren<ClassAssistSelectionProps>> = ({
  industryOptions,
  industryValue,
  onIndustryChange,
  sectorOptions,
  sectorValue,
  onSectorChange,
  children,
}) => {
  const language = useLanguage()
  const {t} = useTranslation()

  const {
    title,
    description,
    industryTitle,
    industrySelectPlaceholder,
    sectorTitle,
    sectorSelectPlaceholder,
  } = getClassAssistContent(language).selection

  return (
    <>
      <div className="wrapper">
        <Text variant={TextVariant.h2} align="center">
          {t(title)}
        </Text>
        <div className="description">
          <Text variant={TextVariant.text20} align="center">
            {t(description)}
          </Text>
        </div>
        <div className="selection">
          <div className="select">
            <div className="label">
              <Text variant={TextVariant.h5} element="p" fontWeight={TextFontWeight.bold}>
                {t(industryTitle)}
              </Text>
            </div>
            <Dropdown
              name="industry"
              value={industryValue ?? undefined}
              options={industryOptions}
              onChange={onIndustryChange}
              placeholder={t(industrySelectPlaceholder)}
            />
          </div>
          <div className="select">
            <div className="label">
              <Text variant={TextVariant.h5} element="p" fontWeight={TextFontWeight.bold}>
                {t(sectorTitle)}
              </Text>
            </div>
            <Dropdown
              name="sector"
              key={industryValue}
              value={sectorValue ?? undefined}
              disabled={industryValue == null}
              options={sectorOptions}
              onChange={onSectorChange}
              placeholder={t(sectorSelectPlaceholder)}
            />
          </div>
        </div>
        {children && <div className="overview">{children}</div>}
      </div>

      <style jsx>{`
        .description {
          margin-top: ${theme.spacing[3.5]};
        }
        .wrapper {
          max-width: ${GUIDE_SELECTION_OVERVIEW_WIDTH};
          margin: 0 auto;
        }
        .selection {
          display: flex;
          justify-content: space-between;
          margin-top: ${theme.spacing[6]};
        }
        .select {
          flex: 1;
        }
        .select + .select {
          margin-left: ${theme.spacing[7]};
        }
        .label {
          margin-bottom: ${theme.spacing[2]};
        }
        .overview {
          width: 100%;
          margin: ${theme.spacing[6]} auto 0;
        }
        @media ${TABLET_SCREENSIZE} {
          .wrapper {
            max-width: inherit;
          }
          .select + .select {
            margin-left: ${theme.spacing[6]};
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .selection {
            display: block;
            margin-top: ${theme.spacing[5]};
          }
          .select + .select {
            margin-left: 0;
            margin-top: ${theme.spacing[4]};
          }
        }
      `}</style>
    </>
  )
}
